import { useNavigate, useParams } from "react-router-dom";
import { routes } from "consts";
import React, { useEffect } from "react";
import { useToaster } from "utils";
import { useQuery } from "@tanstack/react-query";

import { PaziaLoader } from "components/atoms";
import { SurveyPreview } from "survey";
import { HeaderWithBreadcrumbs, PageMain } from "components/molecules";
import { SurveyProvider, useSurvey } from "pages/SurveyCreate/store/reducer";
import { ShSurveyCreator } from "pages/SurveyCreate/ShSurveyCreator";
import { ExamApi, examsApi } from "api/examsApi";
import { RatingQuestionFormValues } from "pages/SurveyCreate/Question/forms/RatingQuestionForm";
import { TextQuestionFormValues } from "pages/SurveyCreate/Question/forms/TextQuestionForm";
import { SelectQuestionFormValues } from "pages/SurveyCreate/Question/forms/SelectQuestionForm";
import { QUERIES_KEYS } from "queries";
import { queryClient } from "main";

export const ExamCreatorPage = () => {
  const { examId } = useParams();

  const { isLoading, data, refetch } = useQuery({
    queryKey: QUERIES_KEYS.SINGLE_EXAM(examId!),
    queryFn: () => examsApi.fetchById(Number(examId)),
    enabled: !!examId,
  });

  if (isLoading) return <PaziaLoader position="center" />;
  if (!data?.data) return null;

  return (
    <SurveyProvider>
      <Page exam={data.data} />
    </SurveyProvider>
  );
};

const Page = ({ exam }: { exam: ExamApi }) => {
  const { examId } = useParams();

  useEffect(() => {
    dispatch({ type: "INITIALIZE_SURVEY", payload: exam.questions });
  }, []);
  const {
    state: { questions, previewQuestionIdx },
    dispatch,
  } = useSurvey();

  const moveNext = () => dispatch({ type: "SET_PREVIEW_QUESTION_IDX", payload: previewQuestionIdx + 1 });
  const moveBack =
    previewQuestionIdx === 0
      ? undefined
      : () => dispatch({ type: "SET_PREVIEW_QUESTION_IDX", payload: previewQuestionIdx - 1 });
  const breadcrumbs = [{ title: "Egzaminy", path: routes.EXAMS }, { title: exam.name }];

  const updateQuestion = async (data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues) => {
    try {
      // @ts-ignore
      await examsApi.updateQuestion(Number(examId), Number(data.id), data);
    } catch (error) {
      console.error(error);
    }
  };

  const createQuestion = async (
    data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues,
  ): Promise<number> => {
    try {
      // @ts-ignore
      const examQuestionId = await examsApi.createQuestion(Number(examId), data);
      queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.SINGLE_EXAM(examId!) });
      return examQuestionId.data;
    } catch (error) {
      console.error(error);
      return 0;
    }
  };

  const onSubmit = async (data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues) => {
    let id = Number(data.id);
    if (data.id) await updateQuestion(data);
    else {
      id = await createQuestion(data);
    }

    queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.SINGLE_EXAM(examId!) });
    return id;
  };

  const onDeleteQuestion = async (id: number) => {
    try {
      await examsApi.deleteQuestion(Number(examId), id);
      queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.SINGLE_EXAM(examId!) });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <PageMain>
      <HeaderWithBreadcrumbs breadcrumbs={breadcrumbs} headerText="Edytuj egzamin" />
      <div className="grid grid-cols-2 gap-10">
        <ShSurveyCreator
          options={["free-text", "multi-select", "rating", "single-select"]}
          onSubmit={onSubmit}
          onDeleteQuestion={onDeleteQuestion}
        />
        <SurveyPreview
          previewQuestionIdx={previewQuestionIdx}
          moveBack={moveBack}
          moveNext={moveNext}
          questions={questions}
        />
      </div>
    </PageMain>
  );
};
